import { useState, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { getBlobFileContent } from '../utils/helpers';
import { loginRequest } from '../auth';
import { AnimatePresence, motion } from 'framer-motion';
import './RejectApplicationPage.css';
import axios from 'axios';


const rejectionRoute = process.env.REACT_APP_REJECT_APPLICATION;
const blobBase = process.env.REACT_APP_BLOB_SAS_BASE;
const blobToken = process.env.REACT_APP_BLOB_SAS_TOKEN;


const RejectApplicationPage = () => {

  const [isReviewing, setIsReviewing] = useState(false);
  const [toast, setToast] = useState({ type: '', message: '' });
  const toastTimeout = useRef(null);
  const [applicantId, setApplicantId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [salesperson, setSalesperson] = useState('');
  const navigate = useNavigate();

  const { accounts, instance } = useMsal();
  const userEmail = accounts[0]?.username;

  const ReasonSelect = ({ value, onChange }) => {
      const [isOpen, setIsOpen] = useState(false);
  
      const containerRef = useRef(null);

      const reasons = [
        "Insufficient or Unavailable Credit History",
        "Incomplete Information Provided",
        "Adverse Past Payment History"
      ]
  
      useEffect(() => {
        const handleClickOutside = (event) => {
          if (containerRef.current && !containerRef.current.contains(event.target)) {
            setIsOpen(false);
          }
        };
  
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
      }, []);
  

  
      return (
        <div
          className="reject-select-container"
          ref={containerRef}
        >
          <label style={{ fontWeight: "bold", marginBottom: "1rem", fontSize: '20px' }}>Reject Reason</label>
          <div
            className="rejection-select-display"
            onClick={() => setIsOpen(prev => !prev)}
            style={{ maxWidth: '100%' }}
          >
            {
              reasons.find((opt) => opt === value) || "Select"
            }
            <span className="arrow">{isOpen ? '▲' : '▼'}</span>
          </div>
  
          <AnimatePresence>
            {isOpen && (
              <motion.ul
                className="reject-options-list"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {reasons.map((option) => (
                  <li key={option} className="reject-select-option" onClick={() => { onChange(option); setIsOpen(false); }}>
                    {option}
                  </li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </div>
      );
    };

  useEffect(() => {
    const fetchName = async () => {
      try {
        const blobURL = `${blobBase}applicants/${applicantId}.json${blobToken}`;
        const content = await getBlobFileContent(blobURL);
        const customer = JSON.parse(content);
        setCustomerName(customer.CustomerName || 'Unknown');
        setCustomerEmail(customer.CustomerEmail);
        setSalesperson(customer.Salesperson);

      } catch (err) {
        console.error('Failed to fetch applicant blob:', err);
      }
    };
  
    if (applicantId.length === 5) {
      fetchName();

      console.log('Fetched customer:', customerName, customerEmail, salesperson);
    } else {
      setCustomerName('');
    }
  }, [applicantId]);


  const handleSubmit = (e) => {
    e.preventDefault();
    setIsReviewing(true);
  }

  const handleModalSubmit = async () => {
    setIsReviewing(false);

    const applicantToReject = {
      applicantId,
      customerName,
      customerEmail,
      salesperson,
      rejectReason
    }

    try {
      // Attach the signed Azure AD token so the backend can verify the caller
      // is on the allow-list — the body alone is not trusted.
      let token;
      try {
        const result = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        token = result.idToken;
      } catch (silentErr) {
        console.error('Silent token acquisition failed, redirecting:', silentErr);
        await instance.acquireTokenRedirect(loginRequest);
        return;
      }

      await axios.post(
        rejectionRoute,
        {applicantToReject, userEmail},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setToast({ type: 'success', message: 'Customer deleted successfully.' });

      setApplicantId('');
    } catch (error) {
      console.error('Error deleting customer:', error);
      const status = error?.response?.status;
      const message =
        status === 401 || status === 403
          ? 'You are not authorized to reject applications.'
          : 'Error deleting customer.';
      setToast({ type: 'error', message });
    }

    setTimeout(() => {
      setToast({ type: '', message: '' });
    }, 5000);    
  };


  return (
    <div className='reject-application-page'>
      <section className='title-section'>
        <h1 >Reject Application</h1>
        <p>Please Note: <strong>Applicant ID</strong> is required.</p>
      </section>


      <div className='reject-div-wrapper'>

        <section className='reject-header-section'>
          <button onClick={() => navigate('/')} className='header-button'>⬅ Back to Dashboard</button>
          <h1 >Rejection Request</h1>
        </section>

        <form onSubmit={handleSubmit} noValidate>
          <section style={{ marginBottom: '20px', textAlign: 'center' }}>
            <label htmlFor='applicantId' style={{ fontWeight: 'bold', fontSize: '18px' }}>
              Applicant ID:
              <input type="text"
                name="applicantId"
                value={applicantId}
                onChange={(e) => setApplicantId(e.target.value.replace(/\D/g, '').slice(0, 5))}
                style={{ marginLeft: '10px', width: '100px', fontSize: '20px', fontWeight: 'bold', textAlign: 'center' }}
                />
            </label>
              {applicantId.length === 5 && customerName  ? (
                <>
                <p style={{ marginTop: '10px', fontWeight: 'bold', fontSize: '18px', color: '#444' }}>
                  Customer on File: <span style={{ fontWeight: 'bold', fontSize: '20px', color: '#0300b6ff' }}>{customerName}</span>
                </p>
                <p>
                  <span style={{ fontWeight: 'bold', fontSize: '18px', color: '#444' }}>Salesperson: </span>
                  <span style={{ fontSize: '20px', color: '#0300b6ff', fontWeight: 'bold' }}>{salesperson || 'N/A'}</span>
                </p>
                <ReasonSelect value={rejectReason} onChange={(value) => {setRejectReason(value);}}/>
                </>
              ) : applicantId.length === 5 && !customerName && (<p style={{ marginTop: '10px', fontWeight: 'bold', color: '#444' }}>
                  Customer on File: <span style={{ fontWeight: 'bold', fontSize: '20px', color: '#b60000ff' }}>Applicant Not Found</span>
                </p>)}
          </section>

          <div className='button-div'>
            <button type="submit" className='reject-confirm-button'>Delete</button>            
          </div>      

        </form>
          </div>

      {isReviewing && (
        <div className="modal-backdrop">
          <div className="process-modal">
            <p><span style={{ color: '#b60000ff', fontWeight: 'bold', fontSize: '16px' }}><u><em>WARNING: THIS ACTION CANNOT BE UNDONE</em></u></span></p>
            <h3>Review Your Request</h3>
            <p><strong>{customerName}</strong></p>
            <ul className="review-list">
              <li><strong>Applicant ID:</strong> {applicantId}</li>
              <li><strong>Customer:</strong> {customerName}</li>
              <li><strong>Salesperson:</strong> {salesperson}</li>
              <li><strong>Reject Reason:</strong> {rejectReason}</li>
            </ul>

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => setIsReviewing(false)}
              >
                Return
              </button>
              <button
                className="reject-confirm-button"
                onClick={(e) => {
                  e.preventDefault(); // prevent default button behavior
                  handleModalSubmit();
                }}
              >
                Confirm and Delete
              </button>


            </div>
          </div>
        </div>
      )}

      {toast.message && (
        <div className={`toast ${toast.type}`}>
          {toast.message}
        </div>
      )}

    </div>
  );
};

export default RejectApplicationPage;

